<template>
    <section>
        <!-- Item List -->
        <MultiStepFilterConfigListItemWidget v-for="(item, index) in listItems"
            class="sm-b-s"
            :key="item.uid"
            :item="item"
            :min="min"
            :selection-count="selectedItems.length"
            :allowSwap="allowSwap"
            :lockedTitle="lockedTitle"
            @onRemove="onRemove(item)"
            @onSwapItem="onSwapItem($event, index)"
            @onEditConfig="onEditConfig($event, item)" />
        <a class="sm-t-m sm-b-s te-link show-more-btn" @click="showAll()" v-if="canMore">{{ showMoreLbl }}</a>
        <!-- Add New Item -->
        <ButtonIcon class="el--s-m"
            icon="plus"
            @click="onAddItem"
            :class="[!addingDisabled ? 'el--t-primary' : '']"
            :disabled="addingDisabled" />
    </section>
</template>
<script>
import { guuid, isArray } from '@aretheyhappy/utils';
import MultiStepFilterPopover from '../multi-step-filter-popover/MultiStepFilterPopover.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import MultiStepFilterConfigListItemWidget from './MultiStepFilterConfigListItemWidget.vue';

export default {
    name: 'mead-multi-step-filter-config-list',
    components: {MultiStepFilterConfigListItemWidget, ButtonIcon},
    props: {
        /**
         * Title/header text shown in the item-selection popover opened when adding or swapping
         * an item; forwarded as the `title` prop to MultiStepFilterPopover.
         */
        title: {type: String, default: 'mead.options' },
        /**
         * The full hierarchical list of selectable items, used to look up the msf_item for each
         * entry in selectedItems and forwarded to the popover opened when adding or swapping items.
         */
        items: {type: Array, default: () => []},
        /**
         * The currently selected MultiStepFilterConfigListItem entries rendered as rows in the list.
         */
        selectedItems: {type: Array, default: () => []},
        /**
         * Items that cannot be selected again; forwarded to the selection popover opened when
         * adding a new item.
         */
        restrictedItems: {type: Array, default: () => []},
        /**
         * Maximum number of items that may be selected. The add button is disabled once this
         * limit is reached; 0 means unlimited.
         */
        max: {type: Number, default: 0},
        /**
         * Minimum number of items that must remain selected; forwarded to each row widget so it
         * can disable removal once the selection would drop below this count.
         */
        min: {type: Number, default: 0},
        /**
         * Whether to show badges with selected-descendant counts on group items in the selection
         * popover.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * Height of the selection popover's scrollable item list.
         */
        uiHeight: {type: [String, Number], default: '40'},

        /**
         * Tooltip to show on locked items.
         */
        lockedTitle: {type: String, default: ''},
        /**
         * Allow items to be swapped (show a chevron on each row and allow clicking it to open the
         * swap popover).
         */
        allowSwap: {type: Boolean, default: true},
        /**
         * Maximum number of selected items shown before the rest are collapsed behind a
         * "show more" link; 0 shows all items.
         */
        maxShown: {type: Number, default: 0},
        /**
         * Whether the add/swap popover should close automatically after an item is selected.
         */
        closeOnSelect: {type: Boolean, default: true}
    },
    data: function() {
        return {
            uid: guuid(),
            swap_index: undefined,
            show_all: false,
        }
    },
    methods: {
        onRemove: function (item) {
            this.$emit('onRemove', item);
        },
        onAdd: function (selection) {
            if (selection.value.length === 0) return;

            const wrappedItem = new MeadCommon.MultiStepFilterConfigListItem()
                .setValue(selection.item.value)
                .setHasConfig(selection.item.hasConfig)
                .setMSTItem(selection.item);

            if (this.swap_index !== undefined)
                this.$emit('onSwap', this.swap_index, wrappedItem)
            else
                this.$emit('onAdd', wrappedItem);

            if (this.closeOnSelect === true)
                MeadCommon.PopoverService.close();
        },
        onSwapItem: function (event, index) {
            this.swap_index = index;
            this.openPopover(event);
        },
        onAddItem: function(event) {
            this.swap_index = undefined;
            this.openPopover(event, this.mode);
        },
        onEditConfig: function (event, item) {
            this.$emit('onEditConfig', event, item);
        },
        openPopover: function (event) {
            const config = new MeadCommon.PopoverConfig()
                .setComponent(MultiStepFilterPopover)
                .setMovable(true)
                .setNoPad(true)
                .setEvent(event)
                .setIndex(1)
                .setProps({
                    title: this.title,
                    pubkey: this.uid,
                    items: this.items,
                    selectedItems: [],
                    restrictedItems: this.restrictedItems,
                    max: this.max,
                    min: this.min,
                    includeGroups: false,
                    allowGroupSelection: false,
                    mode: MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE,
                    showBadgeCounts: this.showBadgeCounts,
                    keepPartialGroups: false,
                    uiHeight: this.uiHeight,
                    startingItem: this.swap_index !== undefined ? this.listItems[this.swap_index].value : undefined
                });
            MeadCommon.PopoverService.open(config);
        },
        findItemWithValue: function (list, value, items=[]) {
            for (const item of list) {
                if (item.value === value) items.push(item);
                if (item.isGroup && items.length === 0) {
                    this.findItemWithValue(item.children, value, items);
                }
            }
        },
        showAll: function () {
            this.show_all = !this.show_all;
        },
    },
    computed: {
        listItems: function () {
            const items = [];
            for (const item of this.selectedItems) {
                let found = [];
                this.findItemWithValue(this.items, item.value, found);
                if (found.length === 1) {
                    item.msf_item = found[0];
                    items.push(item);
                }
            }
            // Limit to this.maxShown items unless show_all is true
            if (this.maxShown > 0) {
                if (!this.show_all && items.length > this.maxShown) {
                    return items.slice(0, this.maxShown);
                }
            }

            return items;
        },
        addingDisabled: function () {
            return this.selectedItems.length >= this.max && this.max !== 0;
        },
        showMoreLbl: function () {
            if (this.show_all) return MeadCommon.I18N.go('general.show_less');

            const totalItems = this.selectedItems.length;
            return MeadCommon.I18N.go('general.show_x_more', {amt: totalItems - this.maxShown});
        },
        canMore: function () {
            return isArray(this.selectedItems) && this.maxShown > 0 && this.selectedItems.length > this.maxShown;
        },
    },
    created() {
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}-mstp-selected`, this.onAdd);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    }
}
</script>

<style lang="scss" scoped>
 .show-more-btn {
    display: block;
 }
</style>